/*
 * SCWT-MAILOUT-DRAFT · Feb 2026 — DRAFT / PREVIEW page.
 *
 * Route: /mail-out-water-testing-kits
 * Status: **noindex + not in nav + not in sitemap** until ops approves.
 * Purpose: showcase the Australia-wide mail-out kit service so ops
 *          can review copy + layout before merging any of this onto
 *          the live homepage.
 *
 * Guardrails (per user brief):
 *   • DOES NOT touch live Home, Booking, Stripe, Starshipit, package
 *     pricing, or any commercial logic.
 *   • Pulls package prices via `TestPackagesStrip` (existing live
 *     component) so no hardcoded prices drift.
 *   • CTA links to the existing booking flow with `?kit=mail_out`
 *     (Booking.jsx already honors this — verified line 399).
 *   • Uses `useSEO({ noindex: true })` — will not appear in Google,
 *     will not appear in `sitemap.xml` since it's not enumerated
 *     there, and is not added to Header nav.
 */
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { TestPackagesStrip } from "@/components/TestPackagesStrip";
import { Button } from "@/components/ui/button";
import { useSEO, SEO_DEFAULTS } from "@/lib/useSEO";
import {
  Truck, Package, FlaskConical, FileText, MapPin, ShieldCheck,
  Droplets, ArrowRight, HeartHandshake, ClipboardList,
} from "lucide-react";


// ── Section: How it works (4-step) ────────────────────────────────
function HowItWorksMailOut() {
  const steps = [
    { icon: ClipboardList, title: "Book your water test online",
      body: "Choose the test package that fits your situation, pay online, and enter the delivery address for your sample kit." },
    { icon: Package,       title: "We post your sample kit",
      body: "Your kit arrives at your door within a few business days — sterile bottles, step-by-step collection instructions and a pre-paid express-post return tube." },
    { icon: Truck,         title: "Collect and return your sample",
      body: "Follow the simple instructions to collect your water sample, then drop the pre-paid express-post return tube in any Australia Post box." },
    { icon: FileText,      title: "Receive your report",
      body: "Once the lab has finished, you receive a clear results report by email — plus a summary of any next steps if issues are flagged." },
  ];
  return (
    <section className="py-20 bg-white" data-testid="mailout-how-it-works">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs uppercase tracking-[0.14em] text-cyan-700 font-semibold">
            How mail-out testing works
          </div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-slate-900">
            Four simple steps, no lab visit required
          </h2>
        </div>
        <div className="grid md:grid-cols-4 gap-6 mt-12">
          {steps.map((s, i) => (
            <div
              key={s.title}
              className="relative rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition"
              data-testid={`mailout-step-${i + 1}`}
            >
              <div className="absolute -top-3 -left-3 h-8 w-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-bold">
                {i + 1}
              </div>
              <s.icon className="w-8 h-8 text-cyan-700" />
              <h3 className="mt-3 font-semibold text-slate-900">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


// ── Section: What's included ──────────────────────────────────────
function WhatsIncluded() {
  const items = [
    { icon: Droplets,   label: "Sample bottle or container" },
    { icon: ClipboardList, label: "Step-by-step sampling instructions" },
    { icon: Package,    label: "Prepaid return packaging / satchel" },
    { icon: FileText,   label: "Booking and reference details for your test" },
  ];
  return (
    <section className="py-20 bg-slate-50" data-testid="mailout-whats-included">
      <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-5 gap-10 items-center">
        <div className="md:col-span-2">
          <div className="text-xs uppercase tracking-[0.14em] text-cyan-700 font-semibold">
            What&rsquo;s inside
          </div>
          <h2 className="mt-2 text-3xl font-bold text-slate-900">
            What&rsquo;s included in your mail-out kit
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Your kit typically includes everything you need to collect the sample properly and return it to our lab. Exact contents vary depending on the test you book — full instructions are in the box.
          </p>
        </div>
        <ul className="md:col-span-3 grid sm:grid-cols-2 gap-3">
          {items.map((it) => (
            <li key={it.label} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4">
              <it.icon className="w-5 h-5 text-cyan-700 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-slate-800">{it.label}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}


// ── Section: Why test your water ──────────────────────────────────
function WhyTest() {
  const reasons = [
    "Tank water used for drinking",
    "Bore water used around the home",
    "Unusual taste, odour or colour",
    "After flooding or heavy rain",
    "After tank cleaning or plumbing changes",
    "Before buying or renting a rural property",
    "Regular annual check",
  ];
  return (
    <section className="py-20 bg-white" data-testid="mailout-why-test">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center">
          <div className="text-xs uppercase tracking-[0.14em] text-cyan-700 font-semibold">
            Common reasons
          </div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-slate-900">
            Why test your water?
          </h2>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {reasons.map((r) => (
            <div key={r} className="flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm text-slate-800">
              <div className="w-1.5 h-1.5 rounded-full bg-cyan-700 mt-2 flex-shrink-0" />
              {r}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


// ── Section: Results + next steps ─────────────────────────────────
function ResultsNextSteps() {
  return (
    <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800 text-white" data-testid="mailout-results">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <div className="text-xs uppercase tracking-[0.14em] text-cyan-300 font-semibold">
          Your report
        </div>
        <h2 className="mt-2 text-3xl sm:text-4xl font-bold">
          Clear results, useful next steps
        </h2>
        <p className="mt-5 text-slate-300 leading-relaxed max-w-2xl mx-auto">
          You&rsquo;ll receive a plain-English report showing every parameter tested. If results do not meet Australian Drinking Water Guidelines, the report will explain what should be addressed and what next steps may be appropriate.
        </p>
        <p className="mt-4 text-slate-300 leading-relaxed max-w-2xl mx-auto">
          Most tank households choose{" "}
          <Link
            to="/aware-tank-360"
            data-testid="mailout-link-tank-360"
            className="font-semibold text-cyan-300 underline decoration-cyan-400/60 underline-offset-2 hover:text-cyan-200"
          >
            rainwater tank testing with AWARE Tank 360
          </Link>{" "}
          for the broader picture of their supply.
        </p>
        <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs text-slate-300">
          <ShieldCheck className="w-4 h-4 text-cyan-300" />
          Reports are prepared for owners, tenants, buyers, agents and property managers.
        </div>
      </div>
    </section>
  );
}


// ── Section: PWS Partner support ──────────────────────────────────
function PartnerSupport() {
  return (
    <section className="py-20 bg-white" data-testid="mailout-partner-support">
      <div className="max-w-5xl mx-auto px-6 rounded-3xl border border-slate-200 bg-gradient-to-br from-emerald-50 to-cyan-50 p-8 sm:p-12">
        <div className="flex flex-col md:flex-row gap-6 md:items-center">
          <div className="md:w-16 h-16 w-16 rounded-2xl bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
            <HeartHandshake className="w-8 h-8" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-[0.14em] text-emerald-700 font-semibold">
              Need help after your results?
            </div>
            <h2 className="mt-1 text-2xl sm:text-3xl font-bold text-slate-900">
              Local support if your water needs work
            </h2>
            <p className="mt-3 text-slate-700 leading-relaxed">
              Where available, we may be able to connect you with a local Pristine Water Systems partner for tank cleaning, filtration, UV systems or other corrective services. Partner availability varies by region.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}


// ── Section: Hero ─────────────────────────────────────────────────
function MailOutHero() {
  return (
    <section
      className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-cyan-950 to-slate-900 text-white"
      data-testid="mailout-hero"
    >
      <div className="absolute inset-0 opacity-[0.05] bg-[radial-gradient(circle_at_20%_20%,white,transparent_40%)]" />
      <div className="relative max-w-6xl mx-auto px-6 pt-24 pb-20 sm:pt-32 sm:pb-24 grid lg:grid-cols-5 gap-10 items-center">
        <div className="lg:col-span-3">
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/25 bg-cyan-300/5 px-3 py-1 text-[11px] uppercase tracking-[0.14em] text-cyan-200 font-semibold">
            <MapPin className="w-3.5 h-3.5" /> Available Australia-wide
          </div>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
            Australia-wide water testing by mail
          </h1>
          <p className="mt-5 text-lg text-slate-300 leading-relaxed max-w-xl">
            Book your test online, receive a sample kit by post, return your water sample to the lab, and get clear results without needing to visit us in person.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              asChild
              className="bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold h-12 px-6 rounded-full"
              data-testid="mailout-hero-primary-cta"
            >
              <Link to="/book-water-test?kit=mail_out">
                Order a mail-out kit <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-white/20 bg-white/5 text-white hover:bg-white/10 h-12 px-6 rounded-full"
              data-testid="mailout-hero-secondary-cta"
            >
              <a href="#packages">View water test packages</a>
            </Button>
          </div>
          <div className="mt-6 text-xs text-slate-400">
            Sample kits can be posted to customers across Australia.
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-6 space-y-4">
            {[
              { icon: Package, label: "Sample kit delivered to your door" },
              { icon: Truck,   label: "Prepaid return satchel included" },
              { icon: FlaskConical, label: "Tested by our lab in Noosaville" },
              { icon: FileText, label: "Clear, actionable report by email" },
            ].map((it) => (
              <div key={it.label} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-cyan-500/15 text-cyan-300 flex items-center justify-center">
                  <it.icon className="w-4.5 h-4.5" />
                </div>
                <span className="text-sm text-slate-200">{it.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}


// ── Section: Final CTA ────────────────────────────────────────────
function FinalMailOutCTA() {
  return (
    <section className="py-24 bg-slate-50" data-testid="mailout-final-cta">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
          Ready to test your water?
        </h2>
        <p className="mt-4 text-slate-600 leading-relaxed max-w-xl mx-auto">
          Order a mail-out water testing kit today. We&rsquo;ll post it out, you send back the sample, and we&rsquo;ll take care of the rest.
        </p>
        <Button
          asChild
          className="mt-8 bg-slate-900 hover:bg-cyan-700 text-white font-semibold h-12 px-8 rounded-full"
          data-testid="mailout-final-cta-btn"
        >
          <Link to="/book-water-test?kit=mail_out">
            Book a mail-out water test <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </Button>
        <div className="mt-4 text-[11px] text-slate-500">
          If you don&rsquo;t see the mail-out option pre-selected, choose it from the kit-method picker on the booking page.
        </div>
      </div>
    </section>
  );
}


// ── Page shell ────────────────────────────────────────────────────
export default function MailOutWaterTestingKits() {
  useSEO({
    title: "Australia-wide Mail-out Water Testing Kits | Aware Water Testing",
    description:
      "Order a water testing kit delivered anywhere in Australia. Book online, receive your sample kit by post, return the sample, and get a clear lab report. Suitable for tank water, bore water, rural properties and holiday homes.",
    url: `${SEO_DEFAULTS.baseUrl}/mail-out-water-testing-kits`,
    // SCWT-MAILOUT-DRAFT · Feb 2026 — draft page, do NOT index until
    // ops approves and moves the section onto the live homepage.
    noindex: true,
  });
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="mailout-page">
      <Header />
      <main>
        <MailOutHero />
        <HowItWorksMailOut />

        <section id="packages" className="pt-4 pb-2 bg-white scroll-mt-20" data-testid="mailout-packages">
          <div className="max-w-6xl mx-auto px-6 text-center pt-12">
            <div className="text-xs uppercase tracking-[0.14em] text-cyan-700 font-semibold">
              Choose your test
            </div>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-slate-900">
              Pick the test that fits your water
            </h2>
            <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
              Prices and inclusions below are pulled directly from our live catalogue so what you see is exactly what applies to your order.
            </p>
          </div>
          <TestPackagesStrip />
        </section>

        <WhatsIncluded />
        <WhyTest />
        <ResultsNextSteps />
        <PartnerSupport />
        <FinalMailOutCTA />
      </main>
      <Footer />
    </div>
  );
}
